import React, { useContext, useEffect } from "react";
import { Box, TextField, Typography, Link } from "@mui/material";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  useKivunovaTranslation,
  KivuI18nContext,
  localizedPath,
  API_RESPONSE_CODE,
  PAGE_ROUTE_LOGIN
} from "@kivunova/kivufrontendcommon";
import { forgotPasswordSchema } from "./constants/forgotPasswordYupSchema";
import { linkButtonSx } from "../common/constants/buttonStyles";
import useForgotPassword from "../../hooks/api/useForgotPassword";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { ForgotPasswordData } from "./types/forgotPasswordTypes";
import AppButton from "../common/AppButton";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { FORGOT_PWD_STEP } from "./Enums/forgotPasswordSteps";
import {
  authFormInputSx,
  authFormStagger,
  authFormItemMotion
} from "../common/constants/authFormStyles";

const ForgotOtpForm: React.FC<{
  setNextStep: (step: FORGOT_PWD_STEP) => void;
  setEmail: (email: string) => void;
}> = ({ setNextStep, setEmail }) => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const emailInputRef = React.useRef<HTMLInputElement>(null);

  const { setAlert } = useContext(AlertBannerContext);
  const forgotPasswordMutation = useForgotPassword();
  const isLoading = forgotPasswordMutation.isPending;

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotPasswordData>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
    mode: "onChange"
  });

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const onSubmit = async (data: ForgotPasswordData) => {
    if (forgotPasswordMutation.isPending) return;

    try {
      await forgotPasswordMutation.mutateAsync(
        { email: data.email },
        {
          onSuccess: (response) => {
            if (response.forgotPassword.code !== API_RESPONSE_CODE.OK) return;
            setEmail(data.email);
            setNextStep(FORGOT_PWD_STEP.RESET);
          }
        }
      );
    } catch {
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR
      });
    }
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      sx={{
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: { xs: "flex-start", md: "center" },
        alignItems: "center",
        p: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", md: "center" }
        }}
      >
        <motion.div
          variants={authFormStagger}
          initial="initial"
          animate="animate"
        >
          {/* ── Title / subtitle ── */}
          <motion.div variants={authFormItemMotion}>
            <Typography
              variant="h5"
              fontWeight="bold"
              textAlign="center"
              sx={{ mb: 1 }}
            >
              {t("auth-forgot-password-title", "Forgot Password")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: { xs: 2, sm: 3 } }}
            >
              {t(
                "auth-forgot-password-description",
                "Enter your email and we'll send you a reset link."
              )}
            </Typography>
          </motion.div>

          {/* ── form-stack: vertical rhythm at 20px → 16px → 12px across breakpoints ── */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="form-stack"
            data-testid="forgot-password-form"
            sx={{ maxWidth: 420, mx: "auto" }}
          >
            <motion.div variants={authFormItemMotion}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={emailInputRef}
                    label={t("email", "Email")}
                    fullWidth
                    placeholder={t(
                      "email-input-placeholder",
                      "Enter your email"
                    )}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                    sx={authFormInputSx}
                    inputProps={{
                      "data-testid": "forgot-password-email",
                      "aria-label": "Email address",
                      "aria-describedby": errors.email
                        ? "email-error"
                        : undefined
                    }}
                  />
                )}
              />
            </motion.div>

            {/*
             * action-btn-row: flex-end on desktop, full-width stacked on mobile.
             * touch-target on AppButton is harmless redundancy — action-btn-row
             * already enforces min-height:44px on mobile via CSS.
             */}
            <motion.div variants={authFormItemMotion}>
              <Box className="action-btn-row">
                <AppButton
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                  data-testid="forgot-password-submit"
                  aria-label="Submit password reset"
                  className="touch-target"
                  sx={{ py: 1.3, px: 3, fontSize: "1rem", borderRadius: 2 }}
                >
                  {isLoading
                    ? t("auth-forgot-password-sending", "Sending...")
                    : t("auth-forgot-password-submit", "Reset Password")}
                </AppButton>
              </Box>
            </motion.div>

            {/* ── Back to login link ── */}
            <motion.div variants={authFormItemMotion}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography variant="body2">
                  {t(
                    "auth-forgot-password-remember-password",
                    "Remember your password?"
                  )}{" "}
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    sx={linkButtonSx}
                    onClick={() =>
                      navigate(
                        localizedPath(PAGE_ROUTE_LOGIN, language, defaultLang)
                      )
                    }
                    data-testid="forgot-password-to-login"
                    aria-label="Return to login page"
                  >
                    {t("auth-forgot-password-login-link", "Login")}
                  </Link>
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default ForgotOtpForm;
